import debounce from 'lodash.debounce';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Modal, TouchableOpacity } from 'react-native';

import ActivityList from '@components/ActivityList/ActivityList';
import { getNextActivityPage } from '@components/ActivityList/activityListHelpers';
import { ActivityTruncationBanner } from '@components/ActivityList/ActivityTruncationBanner';
import { DateRange } from '@components/DateRangePicker/DateRangePickerModal';
import { ContactType } from '@components/Invite/Contact';
import { UpgradeConfirmModal } from '@components/Modals/AnnualConfirmModal.web';
import { ExportActivityTimesheetModal } from '@components/Modals/ExportActivityTimesheetModal';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import SearchInput from '@components/shared/SearchInput/SearchInput.web';
import { Skeleton } from '@components/shared/Skeleton';
import {
  ActivityRowSkeleton,
  SkeletonList,
} from '@components/shared/skeletons';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import {
  PANEL_WIDTH,
  PopupActivityFilterPanel,
} from '@components/Web/PopupActivityFilterPanel.web';
import {
  DATES_PANEL_WIDTH,
  PopupTaskFilterProjectDateRangeV3,
} from '@components/Web/PopupTaskFilterProjectV3.web';
import {
  Audit,
  Project,
  useGetMeQuery,
  useGetPaywallStatusQuery,
  useGlobalActivitySearchLazyQuery,
} from '@graphql/generated';
import { useDebouncedSearch } from '@hooks/useDebouncedSearch';
import { useGetDocumentsByClientIdsQuery } from '@hooks/useGetDocumentsByClientIdsQuery';
import { useOutsideClick } from '@hooks/useOutsideClick.web';
import { useResultsTruncated } from '@hooks/useResultsTruncated';
import { ActivityPaywallFooter } from '@screens/GlobalActivityLog/ActivityPaywallFooter';
import { MIXPANEL_EVENTS, SEARCH_DEBOUNCE_MS } from '@src/constants/constants';
import { useMixpanel } from '@src/utils/mixpanel';
import { TAGS_UPDATED_EVENT } from '@utils/tagEvents';

// ─── Sub-component ────────────────────────────────────────────────────────────

interface ListFooterProps {
  canLoadMore?: boolean;
  loadingMore?: boolean;
}
const ListFooter = ({ canLoadMore, loadingMore }: ListFooterProps) => (
  <>
    {canLoadMore ? (
      <Box mb='listFooter'>{loadingMore && <ActivityIndicatorLoading />}</Box>
    ) : (
      <Box mb='l' />
    )}
  </>
);

// ─── Stale-while-revalidate cache ────────────────────────────────────────────
// Persists the last successful unfiltered page-1 result across remounts so the
// user sees data immediately on re-navigation rather than a full-screen loader.
// Keyed by userId + isPaywalled so:
//   • A different user logging in on the same tab always gets a cold start
//     (no cross-user data leak — logout is an in-place SPA transition).
//   • A paywalled 3-item preview is never shown to an upgraded user (or vice
//     versa).
// Session-scoped: the module variable resets to null on a full page reload.
let _cachedDefaultFirstPage: {
  items: Audit[];
  isPaywalled: boolean;
  userId: string;
} | null = null;

/** Reset the module-level SWR cache. Exported for Jest test isolation only. */
export const _resetActivityCache = (): void => {
  _cachedDefaultFirstPage = null;
};

// ─── Main component ───────────────────────────────────────────────────────────

export const GlobalActivityLog: React.FC = () => {
  const { t } = useTranslation('models');
  const { trackEvent } = useMixpanel();

  const [search, setSearch] = useState('');
  const { checkAuditsDocumentURL } = useGetDocumentsByClientIdsQuery();

  // ─── Filter state ──────────────────────────────────────────────────────────
  const [selectedProjects, setSelectedProjects] = useState<Project[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<ContactType[]>([]);
  const [selectedDueDateRange, setSelectedDueDateRange] = useState<DateRange>({
    start: undefined,
    end: undefined,
  });

  const [isTimesheetModalOpen, setIsTimesheetModalOpen] = useState(false);
  const [isHoveredExportButton, setIsHoveredExportButton] = useState(false);

  // ─── Filter panel popup state ──────────────────────────────────────────────
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterPanel, setFilterPanel] = useState<'main' | 'dates'>('main');
  const [mouseOverFilter, setMouseOverFilter] = useState(false);

  const filterButtonRef = useOutsideClick<HTMLDivElement>(
    useCallback((event: MouseEvent) => {
      const rect = filterPanelRef.current?.getBoundingClientRect();
      const inPanel =
        rect &&
        event.x > rect.x &&
        event.x < rect.x + rect.width &&
        event.y > rect.y &&
        event.y < rect.y + rect.height;
      if (!inPanel) {
        setFilterVisible(false);
        setFilterPanel('main');
      }
    }, [])
  );
  const filterPanelRef = useRef<HTMLDivElement>();

  // Each popup panel is a fixed width (main = PANEL_WIDTH, dates =
  // DATES_PANEL_WIDTH), so we right-align it to the filter button using the
  // constant — no off-screen measurement render needed.
  const panelWidth = filterPanel === 'dates' ? DATES_PANEL_WIDTH : PANEL_WIDTH;

  const adjustFilterBounds = () => {
    if (!filterButtonRef.current) return undefined;
    const client = filterButtonRef.current.getBoundingClientRect();
    return {
      top: client.top + client.height + 4,
      left: client.left + client.width - panelWidth,
    };
  };

  const filterBounds = adjustFilterBounds();

  const PER_PAGE_COUNT = 20;

  const userIds = useMemo(
    () => selectedUsers.map((u) => u.id),
    [selectedUsers]
  );

  const projectIds = useMemo(
    () => selectedProjects.map((p) => p.id),
    [selectedProjects]
  );

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const { data: meData } = useGetMeQuery();
  const currentUserId = meData?.getMe?.id;

  const { data, loading, refetch } = useGetPaywallStatusQuery();
  const isPaywalled = data?.getPaywallStatus?.isPaywalled ?? false;
  const upgradeTeam = data?.getPaywallStatus?.upgradeTeam;

  const badgeNumber =
    selectedProjects.length +
    selectedUsers.length +
    (selectedDueDateRange.start || selectedDueDateRange.end ? 1 : 0);

  const clearAllFilters = () => {
    setSelectedProjects([]);
    setSelectedUsers([]);
    setSelectedDueDateRange({ start: undefined, end: undefined });
  };

  useEffect(() => {
    trackEvent(MIXPANEL_EVENTS.VIEWED_ACTIVITY_LOG, { scope: 'global' });
  }, [trackEvent]);

  useEffect(() => {
    if (badgeNumber > 0) {
      trackEvent(MIXPANEL_EVENTS.FILTERED_ACTIVITY_LOG, {
        project_ids: selectedProjects.map((p) => p.id),
        user_ids: selectedUsers.map((u) => u.id),
        date_start: selectedDueDateRange.start ?? null,
        date_end: selectedDueDateRange.end ?? null,
      });
    }
  }, [selectedProjects, selectedUsers, selectedDueDateRange, trackEvent]);

  // ─── Activity state ────────────────────────────────────────────────────────
  // Seed from the module-level SWR cache so the list is non-empty before the
  // first fetch resolves on remount (eliminates the full-screen loading flash).
  // Only seed when the cached entry belongs to the current user — prevents
  // showing a previous user's data if they logged in on the same tab.
  // isPaywalled is intentionally excluded: useGetPaywallStatusQuery may still
  // be loading (data=undefined, isPaywalled defaults to false) on remount when
  // Apollo's in-memory cache is cold. Including isPaywalled would make a
  // paywalled user's cached 3-item preview miss the seed check, showing a
  // full-screen loader instead of the preview. The effect validates the
  // isPaywalled key once the query settles and clears the list if it mismatches.
  const cacheMatchesCurrentUser =
    _cachedDefaultFirstPage !== null &&
    currentUserId != null &&
    _cachedDefaultFirstPage.userId === currentUserId;
  const [activityList, setActivityList] = useState<Audit[]>(
    cacheMatchesCurrentUser ? _cachedDefaultFirstPage!.items : []
  );
  const [canLoadMore, setCanLoadMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { isTruncated, setTruncatedFromError, resetTruncated } =
    useResultsTruncated();
  const [fetchError, setFetchError] = useState<Error | undefined>();
  // True once the very first page-0 fetch has settled (success or error) —
  // used to skeleton the search input, filter button, and export button
  // themselves so a user can't click one before the activity list has ever
  // appeared (TA-10275). Seeded true on a warm SWR-cache mount (no flash),
  // and once flipped stays true so later debounced searches don't re-lock
  // the controls the user is actively using.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(cacheMatchesCurrentUser);
  const fromRef = useRef(0);
  const fetchGenRef = useRef(0);
  // True on mount when activityList was seeded from cache; consumed once by the
  // fetch-trigger effect to suppress setActivityList([]) so stale data stays
  // visible during the in-flight refresh.
  const skipInitialClearRef = useRef(cacheMatchesCurrentUser);
  // True once the post-mount re-seed path has run (or been determined
  // unnecessary). Initialised to true when the warm-path already handled
  // seeding via the useState initializer, so the re-seed effect is a no-op.
  const hasSeedAttemptedRef = useRef(cacheMatchesCurrentUser);
  // True once the page-1 fetch has settled (success or error). Used by the
  // post-mount re-seed effect to avoid overwriting a fresh, legitimately empty
  // result with stale cached data when the user genuinely has no activity.
  const hasFetchCompletedRef = useRef(false);

  // Value refs — keep doFetch stable while reading latest values
  const userIdsRef = useRef(userIds);
  const projectIdsRef = useRef(projectIds);
  const dateRangeRef = useRef(selectedDueDateRange);
  userIdsRef.current = userIds;
  projectIdsRef.current = projectIds;
  dateRangeRef.current = selectedDueDateRange;
  const isPaywalledRef = useRef(isPaywalled);
  isPaywalledRef.current = isPaywalled;
  const currentUserIdRef = useRef(currentUserId);
  currentUserIdRef.current = currentUserId;
  // Freezes the term actually backing the currently-loaded page(s) — set by
  // the fetch-trigger effect below at the moment it decides to fetch, and
  // read by doFetch for both the page-0 fetch and every load-more page.
  // Reading a live/raw search ref here instead caused wrong-page pagination
  // in Waves 1/3/4 when the user kept typing between a fetch and a
  // load-more scroll (TA-10170).
  const committedTermRef = useRef('');

  const [globalActivitySearch] = useGlobalActivitySearchLazyQuery({
    errorPolicy: 'all',
  });

  // The fetch-trigger effect below already owns debouncing and fetching (it
  // must coordinate with filter/paywall/SWR-cache logic too), so onSearch
  // here is a no-op — useDebouncedSearch is used only for the shared 3-char
  // threshold gate (TA-10170).
  const { isBelowThreshold } = useDebouncedSearch(search, () => {});

  const isNotInList = (item: Audit, list: Audit[]) =>
    !list.some((existing) => existing.id === item.id);

  const checkAuditsDocumentURLRef = useRef(checkAuditsDocumentURL);
  checkAuditsDocumentURLRef.current = checkAuditsDocumentURL;

  const setTruncatedFromErrorRef = useRef(setTruncatedFromError);
  setTruncatedFromErrorRef.current = setTruncatedFromError;
  const resetTruncatedRef = useRef(resetTruncated);
  resetTruncatedRef.current = resetTruncated;

  const doFetch = useCallback(
    async (from: number) => {
      if (from === 0) fetchGenRef.current += 1;
      const myGen = fetchGenRef.current;
      if (from === 0) {
        setIsLoading(true);
        setIsLoadingMore(false);
        setFetchError(undefined);
        resetTruncatedRef.current();
      } else {
        setIsLoadingMore(true);
      }
      try {
        const queryResult = await globalActivitySearch({
          // no-cache: skip stale cache reads entirely to get fresh network data.
          // This component manages its own activityList state, so Apollo cache
          // writes from this query add no value and only introduce stale-data
          // races via cache-and-network.
          fetchPolicy: 'no-cache',
          variables: {
            term: committedTermRef.current,
            userIds: userIdsRef.current,
            projectIds: projectIdsRef.current,
            // Bug fix: convert Dayjs values to ISO strings so the ISO8601DateTime
            // scalar receives a serialised string rather than a Dayjs object.
            startDate: dateRangeRef.current.start?.startOf('day').toISOString(),
            endDate: dateRangeRef.current.end?.endOf('day').toISOString(),
            size: PER_PAGE_COUNT,
            from,
          },
        });
        const data = queryResult.data?.globalActivitySearch;
        if (fetchGenRef.current !== myGen) return;
        if (from === 0) setTruncatedFromErrorRef.current(queryResult);
        if (data) {
          const records = data.map((item) => item.record) as Audit[];
          if (from === 0) {
            checkAuditsDocumentURLRef.current(records);
            setActivityList(records);
            setIsLoading(false);
            setHasLoadedOnce(true);
            hasFetchCompletedRef.current = true;
            // Cache unfiltered, unsearched page-1 results for stale-while-revalidate
            // on remount. Filtered/searched results are never cached so they can't
            // appear under a mismatched filter UI state after remount.
            // Only cache unfiltered, unsearched results when the user identity is
            // known. Skipping the write when currentUserId is undefined prevents a
            // userId:'' sentinel entry that would cause every subsequent same-user
            // remount to miss the cache key check (real userId !== '').
            if (
              currentUserIdRef.current &&
              !committedTermRef.current &&
              userIdsRef.current.length === 0 &&
              projectIdsRef.current.length === 0 &&
              !dateRangeRef.current.start &&
              !dateRangeRef.current.end
            ) {
              _cachedDefaultFirstPage = {
                items: records,
                isPaywalled: isPaywalledRef.current,
                userId: currentUserIdRef.current,
              };
            }
          } else {
            setActivityList((prev) => {
              const newItems = records.filter((r) => isNotInList(r, prev));
              if (newItems.length > 0)
                checkAuditsDocumentURLRef.current(newItems);
              return newItems.length > 0 ? [...prev, ...newItems] : prev;
            });
            setIsLoadingMore(false);
          }
          // Raw-offset paging, not grouped-row paging — TA-10510.
          const page = getNextActivityPage(from, PER_PAGE_COUNT, data);
          fromRef.current = page.nextFrom;
          setCanLoadMore(page.canLoadMore);
        }
      } catch (err: any) {
        if (fetchGenRef.current !== myGen) return;
        hasFetchCompletedRef.current = true;
        setIsLoading(false);
        setIsLoadingMore(false);
        setHasLoadedOnce(true);
        // Historical server-side paywall error string; the current backend
        // returns capped results instead of this error. Kept as a defensive
        // no-op in case any old deployment still emits it.
        if (err?.message !== 'Activities not accessible') {
          setFetchError(err);
        }
      }
    },
    [globalActivitySearch] // checkAuditsDocumentURL accessed via ref — excluded intentionally
  );

  // Keep a ref so the effect below can always call the latest doFetch without
  // including it in the dependency array (avoids re-triggering on every render
  // when upstream hook functions don't have stable references).
  const doFetchRef = useRef(doFetch);
  doFetchRef.current = doFetch;

  // Commits the current trimmed term (or clears it, below threshold) before
  // firing a page-0 fetch — every entrypoint that restarts the list from
  // page 0 outside the main fetch-trigger effect (tab-focus/tags-updated,
  // pull-to-refresh) must go through this instead of calling
  // doFetchRef.current(0) directly, or a refetch that lands while an
  // above-threshold term is still mid-debounce sends a stale/empty term
  // instead of the one the user is actively typing (TA-10170).
  const commitAndFetchPage0Ref = useRef(() => {});
  commitAndFetchPage0Ref.current = () => {
    committedTermRef.current = isBelowThreshold ? '' : search.trim();
    doFetchRef.current(0);
  };

  // Re-fetch when the tab regains focus to recover from stale data after a
  // WebSocket idle disconnect, or when a message's tags are saved so the new
  // audit entry appears immediately without requiring a manual refresh.
  useEffect(() => {
    const handleFocus = () => commitAndFetchPage0Ref.current();
    window.addEventListener('focus', handleFocus);
    window.addEventListener(TAGS_UPDATED_EVENT, handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener(TAGS_UPDATED_EVENT, handleFocus);
    };
  }, []);

  // Trigger fetch on any dependency change; debounce only for text search.
  // The server caps results for paywalled users (returns up to
  // PAYWALLED_MAX_GROUPS items), so we fetch on both entry paths. Skipping the
  // fetch when isPaywalled was already true on mount (e.g. after a tab switch
  // back into Activity) previously left activityList empty and fell through to
  // the "Upgrade to unlock" empty state instead of showing the 3-item preview.
  //
  // isPaywalled is kept in the deps (without an early-return) so that if the
  // user upgrades while staying on this screen — paywall transitions true →
  // false after the subscription-created refetch — the activity list re-fetches
  // and replaces the capped preview with the full feed without a reload.
  // Fire the first fetch and paywall transitions immediately; debounce only
  // user-driven filter edits so rapid successive selections coalesce.
  const isFirstFetchRun = useRef(true);
  const prevPaywalledRef = useRef(isPaywalled);

  useEffect(() => {
    fromRef.current = 0;
    setCanLoadMore(false);
    setIsLoading(true);
    setFetchError(undefined);

    // When stale data was seeded on mount but useGetPaywallStatusQuery hasn't
    // settled yet (loading=true, isPaywalled defaults to false), defer the
    // skip-clear decision so we don't compare against the false default and
    // wrongly clear a paywalled user's cached preview. Once loading resolves,
    // isPaywalled (or loading itself) changes → this effect re-runs and the
    // key comparison uses the real value.
    if (skipInitialClearRef.current && loading) {
      return;
    }

    // On the very first settled effect run after a cache-seeded mount, keep the
    // stale list visible if both keys match. For every subsequent run (filter/
    // search changes, paywall transitions, or no cache on mount) clear normally.
    //
    // Guard on currentUserId: if a different user logged in on the same tab the
    // getMe cache is cold (userId undefined) so we always clear — preventing
    // cross-user data being shown even for a single frame.
    const shouldSkipClear =
      !!currentUserId &&
      skipInitialClearRef.current &&
      _cachedDefaultFirstPage !== null &&
      _cachedDefaultFirstPage.isPaywalled === isPaywalled &&
      _cachedDefaultFirstPage.userId === currentUserId;
    skipInitialClearRef.current = false;
    if (!shouldSkipClear) {
      setActivityList([]);
    }

    const paywallChanged = prevPaywalledRef.current !== isPaywalled;
    prevPaywalledRef.current = isPaywalled;

    // Initial load and paywall true→false (post-upgrade) are not rapid filter
    // edits — fetch immediately so there is no perceptible delay.
    if (isFirstFetchRun.current || paywallChanged) {
      isFirstFetchRun.current = false;
      // Must commit the term the same way the debounced branch below does —
      // otherwise a paywall transition firing while an above-threshold term
      // is still mid-debounce would fetch page 0 with a stale/empty
      // committedTermRef instead of the term the user is actually typing
      // (TA-10170).
      committedTermRef.current = isBelowThreshold ? '' : search.trim();
      doFetchRef.current(0);
      return;
    }

    // Text search debounces SEARCH_DEBOUNCE_MS once at/above the shared
    // 3-char threshold (TA-10170). Below threshold (including empty input)
    // this is functionally a filter-only change, and keeps its own literal
    // 300ms debounce to collapse a burst of project/user/date selections
    // into a single network request — that 300 is deliberately NOT the
    // SEARCH_DEBOUNCE_MS constant. It's a distinct value that happens to
    // numerically coincide today (TA-10048); do not merge the two.
    const delay = isBelowThreshold ? 300 : SEARCH_DEBOUNCE_MS;
    const debounced = debounce(() => {
      committedTermRef.current = isBelowThreshold ? '' : search.trim();
      doFetchRef.current(0);
    }, delay);
    debounced();
    return () => debounced.cancel();
    // doFetch intentionally excluded — all inputs are read via refs inside it
    // loading included so the effect re-runs once getPaywallStatus settles,
    // allowing the deferred skip-clear decision to use the real isPaywalled value
  }, [
    isPaywalled,
    loading,
    isBelowThreshold,
    search,
    userIds,
    projectIds,
    selectedDueDateRange.start,
    selectedDueDateRange.end,
  ]);

  // Post-mount re-seed for cold useGetMeQuery ──────────────────────────────────
  // When useGetMeQuery's in-memory cache is cold on remount, currentUserId is
  // undefined and the useState initializer cannot seed from _cachedDefaultFirstPage
  // (cacheMatchesCurrentUser is false). Once getMe resolves, this effect validates
  // the cache and seeds the list if userId and paywall state both match.
  //
  // Placement: this effect MUST be defined after the fetch-trigger effect above.
  // In React 18+ automatic batching, effects from the same commit run in order and
  // their state updates are batched. When both effects fire together (e.g. when
  // paywall loading resolves with getMe already resolved), setActivityList is
  // called by both: the fetch-trigger first (setActivityList([])), then this
  // effect (setActivityList(staleItems)). The last call wins, so stale data is
  // shown instead of an empty list during the background refresh.
  //
  // hasSeedAttemptedRef prevents double-seeding when getMe was warm on mount —
  // in that case cacheMatchesCurrentUser=true and the ref is initialised to true.
  useEffect(() => {
    if (hasSeedAttemptedRef.current) return;
    if (currentUserId == null) return;

    if (
      _cachedDefaultFirstPage === null ||
      _cachedDefaultFirstPage.userId !== currentUserId
    ) {
      hasSeedAttemptedRef.current = true;
      return;
    }

    // Defer isPaywalled validation until the paywall query settles — same
    // cold-cache concern as the primary fetch-trigger: if getPaywallStatus is
    // still loading, isPaywalled defaults to false and would reject a paywalled
    // user's cached 3-item preview.
    if (loading) return;

    if (_cachedDefaultFirstPage.isPaywalled !== isPaywalled) {
      hasSeedAttemptedRef.current = true;
      return;
    }

    hasSeedAttemptedRef.current = true;
    // Only seed when the mount-fetch has not yet completed. hasFetchCompletedRef
    // is set true in doFetch on both success and error. If it's already true the
    // fresh result (including a legitimately empty list) is the ground truth and
    // must not be overwritten with stale cached data.
    if (!hasFetchCompletedRef.current) {
      setActivityList(_cachedDefaultFirstPage.items);
    }
  }, [currentUserId, isPaywalled, loading]);

  const onLoadMore = () => {
    if (isLoadingMore) return;
    doFetchRef.current(fromRef.current);
  };

  const {
    setMainPanelWidth,
    setShowPaywallTeamSetup,
    subscriptionCreatedModalOpen,
    setUpgradeTeamPlanOpen,
    setUpgradeCTA,
    setTeamValues,
    setUUIdTeamOpen,
    setIdTeamOpen,
  } = useWebDrawer();

  const handleUpgrade = upgradeTeam
    ? () => setShowUpgradeModal(true)
    : () => setShowPaywallTeamSetup(true);
  const paywallButtonLabel = upgradeTeam
    ? t('activity.upgradeToTeam')
    : t('activity.startFreeTrial');

  useEffect(() => {
    if (subscriptionCreatedModalOpen) {
      refetch();
    }
  }, [subscriptionCreatedModalOpen, refetch]);

  // Skeleton the search input, filter button, and export button until the
  // very first page has loaded (or failed) at least once — see
  // hasLoadedOnce above.
  const isInitialMountLoading = !hasLoadedOnce && isLoading;

  return (
    <Box
      flex={1}
      onLayout={(e) => setMainPanelWidth(e.nativeEvent.layout.width)}>
      {/* ─── Header row (always visible) ─────────────────────────────────── */}
      <Box mx='m' my='m' flexDirection='row' alignItems='center'>
        {/* Search input */}
        <Box flex={1} mr='xs'>
          {isInitialMountLoading ? (
            <Skeleton
              testID='global-activity-search-skeleton'
              variant='rect'
              width='100%'
              height={36}
              borderRadius='xs'
            />
          ) : (
            <SearchInput
              height={36}
              onTextChanged={(v) => {
                setSearch(v);
              }}
              value={search}
              onFocus={() => {
                setFilterVisible(false);
                setFilterPanel('main');
              }}
              onCancel={() => {
                setSearch('');
              }}
            />
          )}
        </Box>

        {/* Unified Filter button */}
        {isInitialMountLoading ? (
          <Skeleton
            testID='global-activity-filter-skeleton'
            variant='rect'
            width={76}
            height={36}
            borderRadius='xs'
          />
        ) : (
          <Box ref={filterButtonRef}>
            <TouchableOpacity
              accessibilityLabel='Filter'
              onPress={() => {
                setFilterVisible((v) => !v);
                setFilterPanel('main');
              }}>
              <Box
                height={36}
                flexDirection='row'
                py='xs'
                px='m'
                onMouseEnter={() => setMouseOverFilter(true)}
                onMouseLeave={() => setMouseOverFilter(false)}
                backgroundColor={
                  mouseOverFilter || badgeNumber > 0 || filterVisible
                    ? 'grey01'
                    : 'white'
                }
                borderRadius='xs'
                alignItems='center'
                justifyContent='center'>
                <Icon variant='s' name='AlignCenterDesc' color='black' />
                <Text marginLeft='xs' variant='labelSmall' color='textPrimary'>
                  Filter
                </Text>
                {badgeNumber > 0 && (
                  <Box
                    marginLeft='xs'
                    minWidth={20}
                    height={20}
                    alignItems='center'
                    justifyContent='center'
                    backgroundColor='greenSecondary'
                    borderRadius='s'>
                    <Text variant='webBodyHintSubline' color='white'>
                      {badgeNumber}
                    </Text>
                  </Box>
                )}
                {badgeNumber > 0 && (
                  <Box>
                    <Icon
                      marginLeft='xs'
                      variant='xxxs'
                      name='Close'
                      onPress={(e) => {
                        e?.stopPropagation?.();
                        clearAllFilters();
                      }}
                      color='black'
                    />
                  </Box>
                )}
              </Box>
            </TouchableOpacity>
          </Box>
        )}

        {/* Filter panel modal */}
        <Modal
          visible={filterVisible}
          transparent
          onDismiss={() => {
            setFilterVisible(false);
            setFilterPanel('main');
          }}
          onRequestClose={() => {
            setFilterVisible(false);
            setFilterPanel('main');
          }}>
          <Box
            ref={filterPanelRef}
            style={{ left: filterBounds?.left, top: filterBounds?.top }}
            width={panelWidth}>
            <Box flexShrink={1}>
              {filterPanel === 'main' ? (
                <PopupActivityFilterPanel
                  selectedProjects={selectedProjects}
                  onSelectProject={setSelectedProjects}
                  selectedUsers={selectedUsers}
                  onSelectUser={setSelectedUsers}
                  selectedDueDateRange={selectedDueDateRange}
                  onPressSelectDates={() => setFilterPanel('dates')}
                  onClearAll={clearAllFilters}
                />
              ) : (
                <PopupTaskFilterProjectDateRangeV3
                  selectedDueDateRange={selectedDueDateRange}
                  onSelectDueDateRange={(dr) => {
                    setSelectedDueDateRange(dr);
                    setFilterPanel('main');
                  }}
                  isRangeSelector={true}
                />
              )}
            </Box>
          </Box>
        </Modal>

        {/* Export timesheet button */}
        {isInitialMountLoading ? (
          <Box ml='xxxs'>
            <Skeleton
              testID='global-activity-export-skeleton'
              variant='rect'
              width={150}
              height={36}
              borderRadius='xs'
            />
          </Box>
        ) : (
          <TouchableOpacity
            accessibilityLabel={t('models:activity.exportTimesheet')}
            onPress={() => {
              trackEvent(MIXPANEL_EVENTS.OPENED_TIME_TRACKER);
              setIsTimesheetModalOpen(true);
            }}>
            <Box
              height={36}
              flexDirection='row'
              py='xs'
              px='m'
              ml='xxxs'
              onMouseEnter={() => setIsHoveredExportButton(true)}
              onMouseLeave={() => setIsHoveredExportButton(false)}
              backgroundColor={isHoveredExportButton ? 'grey01' : 'white'}
              borderRadius='xs'
              alignItems='center'
              justifyContent='center'>
              <Icon variant='s' name='FolderPlus' color='black' />
              <Text marginLeft='xs' variant='labelSmall' color='textPrimary'>
                {t('models:activity.exportTimesheet')}
              </Text>
            </Box>
          </TouchableOpacity>
        )}
      </Box>

      {/* ─── Error state ─────────────────────────────────────────────────── */}
      {!isLoading && fetchError && (
        <Box mt='l' alignItems='center'>
          <Text color='textSecondary' textAlign='center'>
            {t('shared:unableToLoadActivityLog')}
          </Text>
        </Box>
      )}

      {/* ─── Loading state (no stale data — show skeleton rows) ──────────── */}
      {/* Covers both the initial mount and the search-debounce window: on
          every dependency change (typed search or filter edit) isLoading
          flips true and activityList is cleared synchronously, so this
          renders for the whole debounce + fetch window, not just on mount. */}
      {isLoading &&
        activityList.length === 0 &&
        (!search || fromRef.current === 0) && (
          <Box>
            {/* Matches ActivityList's styles.dateHeader — a full-width
                grey01 band, not a bare text line — so the skeleton doesn't
                swap to a completely different header shape once real data
                loads. */}
            <Box backgroundColor='grey01' py='xs' px='m' mb='m'>
              <Skeleton variant='text' width={80} />
            </Box>
            <SkeletonList
              row={(index) => <ActivityRowSkeleton key={index} />}
            />
          </Box>
        )}

      {/* ─── Refreshing indicator (stale data visible, fresh fetch in flight) */}
      {isLoading && activityList.length > 0 && (
        <Box height={40}>
          <ActivityIndicatorLoading />
        </Box>
      )}

      {/* ─── Empty state (no activity at all) ────────────────────────────── */}
      {!isLoading &&
        !fetchError &&
        !search &&
        badgeNumber === 0 &&
        activityList.length === 0 && (
          <Box
            mt='l'
            alignItems='center'
            justifyContent='center'
            borderRadius='m'
            flex={1}>
            <Image
              style={{ width: 706, height: 411 }}
              source={require('../../../assets/images/activityEmpty.png')}
            />
            <Box width={526} alignItems='center'>
              <Text
                variant='heading2'
                color='black'
                textAlign='center'
                style={{ marginTop: 67 }}>
                {t('activity.upgradeToUnlockGlobalActivityLog')}
              </Text>
              <Text
                textAlign='center'
                variant='webBody'
                color='textSecondary'
                style={{ marginTop: 14, marginBottom: 22 }}>
                {t('activity.KeepEyesOnEverything')}
              </Text>
              <Box width={163}>
                <Button
                  onPress={() => {
                    const redirectUrl = `${process.env.STRIPE_REDIRECT_BASE_URL}/my-account/subscription-plans`;
                    globalThis.location.href = redirectUrl;
                  }}
                  fullWidth={true}
                  isSmall={true}
                  height={56}
                  backgroundColor='black'
                  accessibilityLabel={t('payments.freeTrialCta')}>
                  <Text
                    variant='labelSmall'
                    color='white'
                    style={{ lineHeight: 16 }}>
                    {t('activity.upgradeNow')}
                  </Text>
                </Button>
              </Box>
            </Box>
          </Box>
        )}

      {/* ─── Empty state (filters active, no results) ────────────────────── */}
      {/* !isLoading is required here so this never renders while a filter
          change's debounced fetch is still in flight (TA-10275) — without
          it, activityList.length === 0 briefly matches this branch AND the
          loading-skeleton branch above simultaneously. */}
      {!search &&
        !loading &&
        !isLoading &&
        badgeNumber > 0 &&
        activityList.length === 0 &&
        (isPaywalled ? (
          <>
            <Box alignItems='center' paddingVertical='xl'>
              <Text color='grey04' textAlign='center'>
                {t('shared:noDataFound')}
              </Text>
            </Box>
            <ActivityPaywallFooter
              onUpgrade={handleUpgrade}
              buttonLabel={paywallButtonLabel}
            />
          </>
        ) : (
          <Box alignItems='center' mt='l'>
            <Text>{t('shared:noDataFound')}</Text>
          </Box>
        ))}

      {/* ─── Activity list ────────────────────────────────────────────────── */}
      {isTruncated && <ActivityTruncationBanner />}
      {(activityList.length > 0 ||
        (!isLoading && !fetchError && (search || badgeNumber > 0))) && (
        <Box flex={1} borderTopWidth={1} borderTopColor='grey02'>
          <ActivityList
            activityType='Global'
            filterValForGlobalActivity={search || undefined}
            refreshing={false}
            audits={activityList}
            onEndReached={canLoadMore && !isPaywalled ? onLoadMore : undefined}
            onRefresh={() => {
              fromRef.current = 0;
              setActivityList([]);
              setCanLoadMore(false);
              commitAndFetchPage0Ref.current();
            }}
            renderEmptyActiveComponent={
              search
                ? () => (
                    <Box alignItems='center' mt='l'>
                      <Text>{t('shared:noDataFound')}</Text>
                    </Box>
                  )
                : undefined
            }
            showFooterWhenEmpty={isPaywalled && !!search}
            listFooterComponent={
              isPaywalled
                ? () => (
                    <ActivityPaywallFooter
                      onUpgrade={handleUpgrade}
                      buttonLabel={paywallButtonLabel}
                    />
                  )
                : () => (
                    <ListFooter
                      canLoadMore={canLoadMore}
                      loadingMore={isLoadingMore}
                    />
                  )
            }
          />
        </Box>
      )}

      {isTimesheetModalOpen && (
        <ExportActivityTimesheetModal
          visible={isTimesheetModalOpen}
          onClose={() => setIsTimesheetModalOpen(false)}
        />
      )}

      <UpgradeConfirmModal
        visible={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        teamName={upgradeTeam?.name}
        memberCount={upgradeTeam?.usersCount ?? 1}
        onContinueToCheckout={(billingCycle) => {
          setTeamValues((prev: any) => ({ ...prev, billingCycle }));
          setUUIdTeamOpen(upgradeTeam?.uuid ?? '');
          setIdTeamOpen(upgradeTeam?.id ?? '');
          setShowUpgradeModal(false);
          setUpgradeCTA(true);
          setUpgradeTeamPlanOpen(true);
        }}
      />
    </Box>
  );
};
